"use client";

import { useEffect } from "react";
import { useBufferState } from "remotion";
import { useSnapshot } from "valtio";

import { useAssetManager } from "./AssetManager";

/**
 * Holds the Player until the card's asset URLs have loaded.
 *
 * `/view` and `/preview` autoplay (#776). `AssetManagerProvider` fetches signed
 * URLs asynchronously and renders the card while `loaded` is still false —
 * images show a fallback, videos render nothing — and its `delayRender()` only
 * holds the Lambda MP4 render, never the browser Player. So on a slow or cold
 * load the opening animation played over missing media (Copilot on #790).
 *
 * Remotion's buffer state is the browser-side equivalent: while any
 * `delayPlayback()` block is active the Player waits, and it carries on from
 * where it stood (frame 0 on first load) once every block is released. It only
 * HOLDS playback, it never starts it, so the editors — which never autoplay —
 * are unaffected.
 *
 * Mounted inside `RootComposition`, not inside `AssetManagerProvider` itself:
 * the provider is also used outside any Player (the artist editor, the home
 * grid), where `delayPlayback()` throws for want of a Remotion context.
 *
 * The media files themselves are held separately, by `pauseWhenBuffering` /
 * `pauseWhenLoading` on the card's <Video>, <Audio> and <Img>.
 */
/**
 * The longest the Player waits for asset URLs before playing anyway.
 *
 * If the provider's loader fails before it sets `loaded`, an unbounded hold
 * would mean the card NEVER plays. Before this hold existed such a card played
 * with gaps; a bounded wait keeps it no worse than that. Ten seconds is far
 * beyond a normal signed-URL fetch and short enough not to read as broken.
 */
export const HOLD_PLAYBACK_MAX_MS = 10_000;

export const HoldPlaybackUntilAssetsLoaded = () => {
  const { loaded } = useSnapshot(useAssetManager());
  const buffer = useBufferState();

  useEffect(() => {
    if (loaded) return;
    const { unblock } = buffer.delayPlayback();
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      unblock();
    };
    const ceiling = setTimeout(release, HOLD_PLAYBACK_MAX_MS);
    // Released when `loaded` flips (this effect re-runs and cleans up), on
    // unmount, or at the ceiling — whichever comes first, and only once.
    return () => {
      clearTimeout(ceiling);
      release();
    };
  }, [loaded, buffer]);

  return null;
};
